<template>
  <teleport to="body">
    <transition name="dw-confirm-fade">
      <div v-if="visible" class="dw-confirm-overlay" @click.self="handleCancel">
        <transition name="dw-confirm-scale">
          <div v-if="visible" class="dw-confirm-box" :class="type">
            <div class="dw-confirm-body">
              <div class="dw-confirm-icon">
                <el-icon :size="24"><WarningFilled v-if="type === 'warning'" /><CircleCheckFilled v-else-if="type === 'success'" /><CircleCloseFilled v-else-if="type === 'danger'" /><InfoFilled v-else /></el-icon>
              </div>
              <div class="dw-confirm-content">
                <div class="dw-confirm-title">{{ title }}</div>
                <div class="dw-confirm-message">{{ message }}</div>
              </div>
            </div>
            <div class="dw-confirm-footer">
              <button class="dw-btn dw-btn--secondary" @click="handleCancel">{{ cancelText }}</button>
              <button class="dw-btn" :class="`dw-btn--${type === 'warning' || type === 'danger' ? 'danger' : 'primary'}`" @click="handleConfirm">{{ confirmText }}</button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref } from 'vue'

let instance = null

export default {
  name: 'ConfirmDialog',
  setup() {
    const visible = ref(false)
    const title = ref('确认')
    const message = ref('')
    const type = ref('warning')
    const confirmText = ref('确认')
    const cancelText = ref('取消')
    let resolvePromise = null
    let rejectPromise = null

    /**
     * 兼容两种调用方式：
     * 1. ElMessageBox 兼容模式: show(message, title, { confirmButtonText, cancelButtonText, type })
     * 2. 对象模式: show({ title, message, type, confirmText, cancelText })
     * 行为与 window.$dwConfirm 一致：取消时 reject('cancel')
     */
    const show = (arg1, arg2, arg3) => {
      let opts = {}
      if (typeof arg1 === 'string') {
        // ElMessageBox 兼容模式: show(message, title, options)
        opts.message = arg1
        opts.title = arg2 || '确认'
        const raw = arg3 || {}
        opts.type = raw.type || 'warning'
        opts.confirmText = raw.confirmButtonText || '确认'
        opts.cancelText = raw.cancelButtonText || '取消'
        // type 'error' -> 'danger' for internal icon mapping
        if (opts.type === 'error') opts.type = 'danger'
      } else {
        // 对象模式
        opts = arg1 || {}
      }

      title.value = opts.title || '确认'
      message.value = opts.message || ''
      type.value = opts.type || 'warning'
      confirmText.value = opts.confirmText || opts.confirmButtonText || '确认'
      cancelText.value = opts.cancelText || opts.cancelButtonText || '取消'
      if (type.value === 'error') type.value = 'danger'
      visible.value = true

      return new Promise((resolve, reject) => {
        resolvePromise = resolve
        rejectPromise = reject
      })
    }

    const hide = () => {
      visible.value = false
    }

    const handleConfirm = () => {
      hide()
      if (resolvePromise) resolvePromise(true)
    }

    const handleCancel = () => {
      hide()
      if (rejectPromise) rejectPromise('cancel')
    }

    instance = { show, hide }

    return {
      visible,
      title,
      message,
      type,
      confirmText,
      cancelText,
      show,
      handleConfirm,
      handleCancel
    }
  }
}

/**
 * useConfirm hook
 * 替代 window.$dwConfirm
 *
 * 用法：
 * const { confirm } = useConfirm()
 * try {
 *   await confirm({ title: '确认', message: '确定删除？', type: 'warning' })
 *   // 用户点了确认
 * } catch {
 *   // 用户点了取消
 * }
 *
 * 或 ElMessageBox 兼容模式：
 * await confirm('确定删除？', '确认', { type: 'warning' })
 */
export function useConfirm() {
  return {
    confirm(arg1, arg2, arg3) {
      if (!instance) return Promise.reject(new Error('ConfirmDialog not mounted'))
      return instance.show(arg1, arg2, arg3)
    }
  }
}
</script>

<style scoped>
.dw-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.dw-confirm-box {
  width: 400px;
  max-width: 90vw;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.dw-confirm-body {
  display: flex;
  gap: 16px;
  padding: 24px;
}

.dw-confirm-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dw-confirm-box.warning .dw-confirm-icon {
  background: #fef3c7;
  color: #d97706;
}

.dw-confirm-box.danger .dw-confirm-icon {
  background: #fee2e2;
  color: #dc2626;
}

.dw-confirm-box.success .dw-confirm-icon {
  background: #d1fae5;
  color: #059669;
}

.dw-confirm-box.info .dw-confirm-icon {
  background: #dbeafe;
  color: #2563eb;
}

.dw-confirm-content {
  flex: 1;
  min-width: 0;
}

.dw-confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 6px;
}

.dw-confirm-message {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.dw-confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f3f4f6;
}

.dw-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.dw-btn--secondary {
  background: #f3f4f6;
  color: #374151;
}

.dw-btn--secondary:hover {
  background: #e5e7eb;
}

.dw-btn--primary {
  background: #4f46e5;
  color: #fff;
}

.dw-btn--primary:hover {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

.dw-btn--danger {
  background: #fee2e2;
  color: #dc2626;
}

.dw-btn--danger:hover {
  background: #fecaca;
  transform: translateY(-1px);
}

/* 动画 */
.dw-confirm-fade-enter-active,
.dw-confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dw-confirm-fade-enter-from,
.dw-confirm-fade-leave-to {
  opacity: 0;
}

.dw-confirm-scale-enter-active,
.dw-confirm-scale-leave-active {
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease;
}

.dw-confirm-scale-enter-from,
.dw-confirm-scale-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}
</style>
